import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { MasterDataMappingsLayout } from "./components/master-data-mappings/MasterDataMappingsLayout";
import { isMasterDataEntityType, MASTER_DATA_ENTITY_TYPES } from "./lib/enums";
import { requireAdmin, requireAuth } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { AuditLogPage } from "./routes/dashboard/audit-log";
import { BuInstancesPage } from "./routes/dashboard/bu-instances";
import { ClientSearchPage } from "./routes/dashboard/client-search";
import { ContractSearchPage } from "./routes/dashboard/contract-search";
import { CopyOperationDetailPage } from "./routes/dashboard/copy-operations/$id";
import { CopyOperationsLogPage } from "./routes/dashboard/copy-operations/index";
import { MasterDataMappingsEntityPage } from "./routes/dashboard/master-data-mappings/$entityType";
import { PrivateHireSearchPage } from "./routes/dashboard/private-hire-search";
import { ProfilePage } from "./routes/dashboard/profile";
import { SettingsApiLogsPage } from "./routes/dashboard/settings/api-logs";
import { SettingsEmailPage } from "./routes/dashboard/settings/email";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { SoapCallLogPage } from "./routes/dashboard/soap-call-log";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: async (ctx) => {
    await requireAuth();
    if (ctx.location.pathname === "/dashboard") {
      throw redirect({ to: "/dashboard/contract-search" });
    }
  },
  component: DashboardLayoutRoute,
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/profile",
  component: ProfilePage,
});

const contractSearchRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/contract-search",
  component: ContractSearchPage,
});

const clientSearchRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/client-search",
  component: ClientSearchPage,
});

const privateHireSearchRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/private-hire-search",
  component: PrivateHireSearchPage,
});

const copyOperationsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/copy-operations",
  component: CopyOperationsLogPage,
});

const copyOperationDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/copy-operations/$id",
  component: CopyOperationDetailPage,
});

const buInstancesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/bu-instances",
  beforeLoad: requireAdmin,
  component: BuInstancesPage,
});

function MasterDataMappingsLayoutRoute() {
  return (
    <MasterDataMappingsLayout>
      <Outlet />
    </MasterDataMappingsLayout>
  );
}

// Read-only for any authenticated user (requireAuth, not requireAdmin) — see
// MasterDataMappingsTable's own isAdmin gating for mutation affordances.
const masterDataMappingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/master-data-mappings",
  beforeLoad: async (ctx) => {
    await requireAuth();
    if (ctx.location.pathname === "/dashboard/master-data-mappings") {
      throw redirect({
        to: "/dashboard/master-data-mappings/$entityType",
        params: { entityType: MASTER_DATA_ENTITY_TYPES[0] },
      });
    }
  },
  component: MasterDataMappingsLayoutRoute,
});

const masterDataMappingsEntityRoute = createRoute({
  getParentRoute: () => masterDataMappingsLayout,
  path: "/$entityType",
  beforeLoad: ({ params }) => {
    if (!isMasterDataEntityType(params.entityType)) {
      throw redirect({
        to: "/dashboard/master-data-mappings/$entityType",
        params: { entityType: MASTER_DATA_ENTITY_TYPES[0] },
      });
    }
  },
  component: MasterDataMappingsEntityPage,
});

const soapCallLogRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/soap-call-log",
  beforeLoad: requireAdmin,
  component: SoapCallLogPage,
});

const auditLogRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/audit-log",
  beforeLoad: requireAdmin,
  component: AuditLogPage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin();
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsEmailRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/email",
  component: SettingsEmailPage,
});

const settingsApiLogsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/api-logs",
  component: SettingsApiLogsPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  dashboardLayout.addChildren([
    profileRoute,
    contractSearchRoute,
    clientSearchRoute,
    privateHireSearchRoute,
    copyOperationsRoute,
    copyOperationDetailRoute,
    buInstancesRoute,
    masterDataMappingsLayout.addChildren([masterDataMappingsEntityRoute]),
    soapCallLogRoute,
    auditLogRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsSystemRoute,
      settingsEmailRoute,
      settingsApiLogsRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
